import {
  Role,
  RoleStr,
  User,
  UserStatusActionType,
  UserStatusType,
  Subscription,
  ReadOnlyUser,
  Permission,
} from 'v2/models';
import { fetchUserSubscriptions } from '../../actions';
import {
  hasPermission,
  PERMISSION_NAME,
  hasActivePermissions,
} from './permissions';

export const getUserRole = (user: Partial<User>): Role => {
  if (user?.nomad_admin) {
    return 'nomadAdmin';
  }
  if (user?.data_provider) {
    return 'dataProvider';
  }
  if (user?.organization) {
    return 'dataBuyer';
  }
  return '';
};

export const getUserRoleStr = (role: Role): RoleStr => {
  switch (role) {
    case 'dataProvider':
      return 'Data Provider';
    case 'dataBuyer':
      return 'Data Buyer';
    case 'nomadAdmin':
      return 'Nomad Admin';
    default:
      return '';
  }
};

export const getDefaultUrlPerRole = (
  user: User,
  permissions: { [key: string]: Permission }
) => {
  const DEFAULT_PAGE_PER_ROLE = {
    dataProvider: '/data-requests/active',
    dataBuyer: '/',
    nomadAdmin: user.ghosting_admin ? '/all-users' : '/top-providers',
  };

  if (!user.active) {
    return '/inactive';
  }

  const drmPermissionAllowed = hasPermission(permissions, [
    PERMISSION_NAME.DRM,
  ]);

  if (
    !hasActivePermissions(permissions) ||
    hasRequestsPermission(permissions)
  ) {
    DEFAULT_PAGE_PER_ROLE.dataBuyer = '/requests';
  } else if (drmPermissionAllowed) {
    DEFAULT_PAGE_PER_ROLE.dataBuyer = '/no-data-providers/home';
  }

  const role: Role = getUserRole(user);
  if (role) {
    return DEFAULT_PAGE_PER_ROLE[role];
  } else {
    return '/settings/account';
  }
};

export const USER_STATUSES: UserStatusType[] = ['Admin', 'Member', 'Disabled'];
export const USER_STATUSES_ACTIONS: UserStatusActionType[] = [
  'Admin',
  'Member',
  'Disable',
];

export function getUserStatus(user: ReadOnlyUser): UserStatusType {
  if (!user.active) {
    return 'Disabled';
  }
  return user.admin ? 'Admin' : 'Member';
}

export function getUserStatusChangeAction(
  user: ReadOnlyUser
): UserStatusActionType {
  if (!user.active) {
    return 'Disable';
  }
  return user.admin ? 'Admin' : 'Member';
}

/**
 *
 * @param user
 * @returns Boolean indicating wheter or not the user is the owner of any of his subscriptions
 */
export const isSubscriptionOwner = async (user: User) => {
  const subscriptions_fetched = await fetchUserSubscriptions().then(
    (data) => data.results
  );

  const user_subscriptions = subscriptions_fetched.filter(
    (subscription: Subscription) => {
      return (
        subscription.users.includes(user.id) && subscription.owner === user.id
      );
    }
  );

  return user_subscriptions.length > 0 ? true : false;
};

export const hasRequestsPermission = (permissions: {
  [key: string]: Permission;
}): boolean =>
  hasPermission(permissions, [
    PERMISSION_NAME.CREATE_SEARCH,
    PERMISSION_NAME.CREATE_INTERNAL_SEARCH,
    PERMISSION_NAME.CREATE_SEARCH_OWN,
    PERMISSION_NAME.CREATE_INTERNAL_SEARCH_OWN,
    PERMISSION_NAME.REVIEW_SEARCH_ORG,
    PERMISSION_NAME.REVIEW_SEARCH_MASTER_ORG,
    PERMISSION_NAME.MONITOR_SEARCHES_ORG,
    PERMISSION_NAME.MONITOR_SEARCHES_MASTER_ORG,
    PERMISSION_NAME.CREATE_SEARCH_FREE,
  ]);

export const isRequestViewerOnly = (permissions: {
  [key: string]: Permission;
}): boolean =>
  hasPermission(permissions, [
    PERMISSION_NAME.VIEW_SEARCHES_MASTER_ORG,
    PERMISSION_NAME.VIEW_SEARCHES_ORG,
  ]) && !hasRequestsPermission(permissions);
